import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Archive, ArchiveRestore, Check, Pencil, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';
import type { ActionEntity } from '../hooks/useScopedActionForm';
import { useTaskFieldEdit } from './TaskFieldEditContext';

/**
 * Only reachable when the drawer is open inside the map provider, and it pulls
 * in ArcGIS, so it is fetched on demand rather than with every ticket.
 */
const ActionMapButton = lazy(() =>
  import('../ActionMapButton').then((mod) => ({ default: mod.ActionMapButton }))
);

type ActionTicketLayoutProps = {
  /** Absent while creating, so the header shows the pending ID instead. */
  entity?: ActionEntity;
  displayId?: string | null;
  archived?: boolean;
  onArchiveClick?: () => void;
  /** DETAILS rail: above the body when narrow, beside it when wide. */
  details: React.ReactNode;
  /** Description and the row-editor sections. */
  children: React.ReactNode;
};

/**
 * Ticket chrome for a single action: an identity header, a main column, and a
 * DETAILS rail.
 *
 * The rail is a second grid column only from `lg` up. Below that — a phone, or
 * the tracker squeezed next to an open map — it collapses above the body, which
 * keeps the header's map icon button and every field reachable without
 * horizontal scrolling.
 */
export const ActionTicketLayout: React.FC<ActionTicketLayoutProps> = ({
  entity,
  displayId,
  archived,
  onArchiveClick,
  details,
  children,
}) => {
  // The drawer opens from the tracker (inside the map provider) and from
  // IAP-only surfaces that have no map at all.
  const hasMap = useOptionalUscgMapContext() !== null;
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { autoPersist, persistTip, editingFieldKey, beginEdit, endEdit } =
    useTaskFieldEdit();
  const nameError = errors.name?.message as string | undefined;
  const isEditingName = autoPersist && editingFieldKey === 'name';
  const [nameDraft, setNameDraft] = useState(() =>
    String(getValues('name') ?? '')
  );

  useEffect(() => {
    if (isEditingName) {
      setNameDraft(String(getValues('name') ?? ''));
    }
  }, [getValues, isEditingName]);

  const nameInputClassName =
    'tw:h-auto tw:border-0 tw:bg-transparent tw:px-0 tw:text-base tw:font-semibold tw:shadow-none tw:hover:border-transparent';

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <header className="tw:flex tw:min-w-0 tw:items-start tw:justify-between tw:gap-2 tw:border-b tw:border-border tw:pb-3">
        <div className="tw:min-w-0 tw:flex-1">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
            <span className="tw:font-mono tw:text-xs tw:text-muted-foreground">
              {displayId || 'ID assigned on save'}
            </span>
            {archived && <Badge variant="outline">Deactivated</Badge>}
          </div>
          <Field
            data-invalid={nameError ? true : undefined}
            className="tw:mt-1 tw:gap-1"
          >
            <div className="tw:flex tw:items-start tw:gap-1">
              <div className="tw:min-w-0 tw:flex-1">
                <label htmlFor="name" className="tw:sr-only">
                  Task
                </label>
                {!autoPersist ? (
                  <Input
                    id="name"
                    placeholder="Untitled task"
                    aria-invalid={nameError ? true : undefined}
                    className={nameInputClassName}
                    {...register('name', {
                      required: {
                        value: true,
                        message: 'This field is required',
                      },
                      maxLength: {
                        value: 250,
                        message: 'This maximum length is 250 characters',
                      },
                    })}
                  />
                ) : isEditingName ? (
                  <Input
                    id="name"
                    placeholder="Untitled task"
                    aria-invalid={nameError ? true : undefined}
                    className={nameInputClassName}
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                  />
                ) : (
                  <p
                    id="name"
                    className="tw:m-0 tw:text-base tw:font-semibold"
                  >
                    {String(getValues('name') ?? '') || (
                      <span className="tw:text-muted-foreground">
                        Untitled task
                      </span>
                    )}
                  </p>
                )}
              </div>
              {autoPersist ? (
                isEditingName ? (
                  <div className="tw:flex tw:shrink-0 tw:gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Save task name"
                      onClick={() => {
                        setValue('name', nameDraft, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        endEdit();
                        void persistTip();
                      }}
                    >
                      <Check className="tw:size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Cancel task name"
                      onClick={() => {
                        setNameDraft(String(getValues('name') ?? ''));
                        endEdit();
                      }}
                    >
                      <X className="tw:size-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Edit task name"
                    onClick={() => beginEdit('name')}
                  >
                    <Pencil className="tw:size-4" />
                  </Button>
                )
              ) : null}
            </div>
            <FieldError>{nameError}</FieldError>
          </Field>
        </div>
        <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
          {hasMap && entity && (
            <Suspense fallback={null}>
              <ActionMapButton action={entity} />
            </Suspense>
          )}
          {entity && onArchiveClick && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              title={archived ? 'Reactivate task' : 'Deactivate task'}
              aria-label={archived ? 'Reactivate task' : 'Deactivate task'}
              onClick={onArchiveClick}
            >
              {archived ? <ArchiveRestore /> : <Archive />}
            </Button>
          )}
        </div>
      </header>

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="tw:order-2 tw:flex tw:min-w-0 tw:flex-col tw:gap-3 tw:lg:order-1">
          {children}
        </div>
        <aside className="tw:order-1 tw:min-w-0 tw:lg:order-2">{details}</aside>
      </div>
    </div>
  );
};
